// Settings Service for TradeMate Pro
// Provides centralized access to company settings, rates, and configurations
// Updated to use proper Supabase client with user authentication (industry standard)

import { supabase, COMPANY_DATA_WRITE_EVENT } from '../utils/supabaseClient';
import { setCompanyTimeZone, setCompanyDateFormat, setCompanyTimeFormat, companyDateKey } from '../utils/dateFormatters';
import { normalizePhone } from '../utils/phone';
import { DEFAULT_PAYMENT_TERMS } from '../utils/paymentTerms';

// Cross-tab signal: another tab wrote company data, drop the cached copies here too.
const CACHE_BUST_KEY = 'tm_company_data_written_at';

// Writable column set of the `settings` view (the base is `company_settings`).
// Derived from the LIVE schema 2026-06-18. The `settings` view is updatable, but it
// only exposes these columns — sending ANY other key to `.from('settings').update()`
// makes PostgREST reject the ENTIRE statement (PGRST204), which previously caused whole
// Settings tabs (Notifications, Security, Invoicing) to silently fail to save. We filter
// every settings write to this set so valid fields persist and unknown keys are dropped
// (with a console.warn) instead of poisoning the save.
// To regenerate: node db-query.js "SELECT column_name FROM information_schema.columns WHERE table_schema='public' AND table_name='settings' ORDER BY column_name"
const SETTINGS_VIEW_COLUMNS = new Set([
  'allow_customer_scheduling', 'allow_partial_deposits', 'allow_partial_payments', 'audit_log_retention_days',
  'auto_approve_customer_selections', 'auto_generate_work_orders', 'auto_invoice_on_completion', 'auto_logout_enabled',
  'auto_schedule_after_approval', 'auto_send_rejection_email', 'business_hours_end', 'business_hours_start',
  'cancellation_fee', 'company_id', 'created_at', 'currency', 'currency_symbol', 'date_format',
  'default_buffer_after_minutes', 'default_buffer_before_minutes', 'default_cancellation_policy',
  'default_invoice_notes', 'default_invoice_terms', 'default_payment_terms',
  'default_tax_rate', 'deposit_enabled', 'deposit_fixed_amount', 'deposit_percent', 'deposit_type',
  'device_tracking_enabled', 'diagnostic_fee', 'email_notifications_enabled', 'emergency_rate_multiplier',
  'enable_customer_self_scheduling', 'failed_login_notifications', 'holiday_multiplier', 'id',
  'in_app_notifications_enabled', 'invoice_footer', 'invoice_number_prefix', 'ip_whitelist', 'ip_whitelist_enabled',
  'job_buffer_minutes', 'labor_rate', 'late_fee_rate', 'lockout_duration_minutes', 'loyalty_discount_percentage',
  'max_advance_booking_days', 'max_login_attempts', 'military_discount_percentage',
  'min_advance_booking_hours', 'min_password_length', 'minimum_service_charge', 'minimum_travel_charge', 'multi_location_enabled',
  'next_invoice_number', 'next_po_number', 'overtime_multiplier', 'material_markup', 'password_breach_checking',
  'password_expiry_days', 'payment_instructions', 'po_allow_partial_receiving', 'po_approval_threshold',
  'po_auto_numbering', 'po_auto_send_to_vendor', 'po_currency', 'po_default_notes', 'po_default_shipping_method',
  'po_default_terms', 'po_email_template', 'po_footer_text', 'po_number_prefix', 'po_overdue_notification_days',
  'po_payment_terms_options', 'po_reminder_days', 'po_require_approval', 'po_require_receipt_confirmation',
  'po_tax_calculation_method', 'preferred_contact_method', 'push_notifications_enabled', 'quality_control_enabled',
  'rejection_follow_up_enabled', 'require_customer_approval', 'require_deposit_before_scheduling',
  'require_deposit_on_approval', 'require_https', 'require_numbers', 'require_photo_documentation',
  'require_signature_on_approval', 'require_special_chars', 'require_terms_acceptance', 'require_uppercase',
  'scheduling_buffer_hours', 'security_headers_enabled', 'send_auto_reminders', 'send_invoice_notifications',
  'send_quote_notifications', 'senior_discount_percentage', 'session_timeout_minutes', 'show_logo_on_invoices',
  'show_notes_on_invoices', 'show_technician_names', 'show_terms_on_invoices', 'signature_type',
  'sms_notifications_enabled', 'stripe_enabled', 'stripe_public_key', 'stripe_secret_key', 'subcontractor_markup',
  'suspicious_activity_alerts', 'terms_and_conditions_text', 'terms_version', 'time_format', 'timezone',
  'travel_charge_per_mile', 'updated_at', 'weekend_multiplier', 'working_days'
]);

// Keep only keys that are real `settings` columns; return the kept payload + any dropped
// keys (so callers/console can surface UI fields that have no backing column rather than
// silently failing the whole UPDATE).
function filterToSettingsColumns(updates) {
  const kept = {};
  const dropped = [];
  for (const key of Object.keys(updates)) {
    if (SETTINGS_VIEW_COLUMNS.has(key)) kept[key] = updates[key];
    else dropped.push(key);
  }
  return { kept, dropped };
}

class SettingsService {
  constructor() {
    this.cachedBusinessSettings = null;
    this.cachedIntegrations = null;
    this.lastFetch = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    // Short on purpose: long enough to absorb a sign-in or page-load burst, short enough that
    // a change made on ANOTHER device (which no event here can see) reaches PDFs and emails
    // within a minute. Same-browser edits invalidate immediately.
    this.companyProfileTtl = 60 * 1000;
    // In-flight request coalescing. A TTL cache alone does NOT fix the reported symptom:
    // eight components mount in the same tick, all miss the empty cache before any response
    // arrives, and all eight fire. Keyed by method+companyId, cleared when the request
    // settles — so it collapses a simultaneous stampede without ever serving a stale value
    // that outlives the request.
    this.inFlight = new Map();
    // Company profile: ONE read per session, shared by every surface (sign-in, brand,
    // integrations, invoices, emails, PDFs). { companyId, data, at } | null.
    this.companyProfileCache = null;
    // Bumped by clearCache(). A read that started before a write must not repopulate the
    // cache with the pre-write row when it lands after the write.
    this.generation = 0;
    if (typeof window !== 'undefined') {
      // Any successful write to companies / settings / company_settings from THIS tab (see
      // COMPANY_DATA_WRITE_EVENT in utils/supabaseClient.js) drops the caches, and tells
      // the other tabs to do the same via a localStorage key.
      window.addEventListener(COMPANY_DATA_WRITE_EVENT, () => {
        this.clearCache();
        try { localStorage.setItem(CACHE_BUST_KEY, String(Date.now())); } catch { /* no storage */ }
      });
      window.addEventListener('storage', (e) => {
        if (e.key === CACHE_BUST_KEY) this.clearCache();
      });
    }
  }

  /**
   * Share one request between callers that ask for the same thing at the same time.
   * Board #3248: a Notifications page load issued 10 GETs to /companies, 5 to /profiles and
   * 3 to /settings — the same rows, concurrently, because every component asks for itself.
   */
  _coalesce(key, run) {
    const existing = this.inFlight.get(key);
    if (existing) return existing;
    // Delete only OUR entry: clearCache() may have replaced it with a newer request.
    const p = (async () => run())().finally(() => {
      if (this.inFlight.get(key) === p) this.inFlight.delete(key);
    });
    this.inFlight.set(key, p);
    return p;
  }

  // Sanitize data to meet database constraints
  sanitizeData(data) {
    const sanitized = { ...data };

    // Convert empty strings to null for all fields
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] === '') {
        sanitized[key] = null;
      }
    });

    // Phone: must be E.164 format or null.
    //
    // Board #2114 (same class as the onboarding copy). This tested `digits.length === 10`
    // BEFORE it tested for a leading "+", so a number that already named its country
    // never reached that branch: "+4712345678" — a perfectly ordinary Norwegian number,
    // 8 national digits, 10 in E.164 — was stored as "+14712345678". Denmark (+45) and
    // every other 2-digit code with an 8-digit national number are the same.
    //
    // normalizePhone (utils/phone.js) is the canonical owner and honours an explicit "+"
    // first. The US default only applies when the user named no country at all.
    if (sanitized.phone !== null && sanitized.phone !== undefined) {
      sanitized.phone = normalizePhone(sanitized.phone);
    }

    // Email: must be valid format or null
    if (sanitized.email !== null && sanitized.email !== undefined) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized.email)) {
        sanitized.email = null;
      }
    }

    return sanitized;
  }

  // Unified update entrypoint used by multiple Settings tabs (Notifications, Invoicing UI, etc.)
  // Routes fields to the correct tables and creates settings rows if missing.
  async updateSettings(companyId, updates) {
    try {
      if (!companyId) throw new Error('Missing companyId');

      // Sanitize all data before processing
      const sanitizedUpdates = this.sanitizeData(updates);

      // company_settings is the SINGLE home for these. `invoice_number_prefix`,
      // `next_invoice_number`, and `timezone` are real company_settings columns (exposed
      // by the `settings` view), so they flow straight to the settings update below.
      // The old mirror-write onto companies.invoice_prefix/invoice_start_number/timezone
      // was a legacy duplicate (the numbering RPC generate_document_number and the
      // smart-scheduling function already read company_settings) — removed to stop drift.
      const businessUpdates = { ...sanitizedUpdates };

      // Ensure settings row exists
      let { data: existing, error: selErr } = await supabase
        .from('settings')
        .select('company_id')
        .eq('company_id', companyId)
        .limit(1);
      if (selErr) {
        console.warn('updateSettings: settings existence check failed', selErr);
      }

      if (!existing || existing.length === 0) {
        // Insert a new row (empty) to upsert settings into
        const { error: insErr } = await supabase
          .from('settings')
          .insert({ company_id: companyId, created_at: new Date().toISOString() });
        if (insErr) {
          console.warn('updateSettings: failed to insert settings shell row', insErr);
        }
      }

      // 4) Update settings with remaining fields (filtered to real columns so an
      //    unknown UI key can't reject the whole UPDATE → silent save failure)
      const { kept: settingsUpdates, dropped } = filterToSettingsColumns(businessUpdates);
      if (dropped.length > 0) {
        console.warn('updateSettings: dropped non-settings columns (no backing column):', dropped);
      }
      if (Object.keys(settingsUpdates).length > 0) {
        const { error: updErr } = await supabase
          .from('settings')
          .update({ ...settingsUpdates, updated_at: new Date().toISOString() })
          .eq('company_id', companyId);
        if (updErr) {
          console.error('❌ updateSettings: settings update failed', updErr);
          return false;
        }
      }

      // Clear caches so subsequent loads reflect new values
      this.clearCache();

      // #3491: publish display settings the moment they are saved. Formatters read module
      // globals set at sign-in, so a new timezone (or date/time format) left every open
      // dashboard and invoice rendering the OLD one until a reload or a new session.
      if (settingsUpdates.timezone) setCompanyTimeZone(settingsUpdates.timezone);
      if (settingsUpdates.date_format) setCompanyDateFormat(settingsUpdates.date_format);
      if (settingsUpdates.time_format) setCompanyTimeFormat(settingsUpdates.time_format);
      return true;
    } catch (e) {
      console.error('❌ updateSettings failed:', e);
      return false;
    }
  }

  /**
   * The company's default line-item display for customer-facing quotes and invoices (#2979).
   *
   * Read off the BASE company_settings table, not the `settings` view: that view has an
   * explicit column list, so a newly added column is invisible through it. Same treatment as
   * business_hours and the document counters (CLAUDE.md §17).
   *
   * ONE reader, shared by the quote seed and the invoice freeze, so the two cannot disagree
   * about what the company default is. Returns null when it cannot be read — every caller
   * already falls back to the enum's own default, BILLABLE_ITEMS.
   */
  async getDefaultInvoiceVisibilityPolicy(companyId) {
    if (!companyId) return null;
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .select('default_invoice_visibility_policy')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      return data?.default_invoice_visibility_policy || null;
    } catch (e) {
      console.error('Could not read the default line-item display policy:', e);
      return null;
    }
  }

  // Get business settings from company_settings table (following locked schema)
  // { fresh: true } skips the cached copy (sign-in uses it so a new session never starts
  // from an older one's values) and refills the cache for everyone else.
  async getBusinessSettings(companyId, { fresh = false } = {}) {
    const now = Date.now();

    // Return cached settings if still valid
    if (
      !fresh &&
      this.cachedBusinessSettings &&
      this.cachedBusinessSettings.company_id === companyId &&
      this.lastFetch &&
      now - this.lastFetch < this.cacheTimeout
    ) {
      return this.cachedBusinessSettings;
    }

    // Board #3248. The TTL cache above already stops the REPEATS, but not the stampede: on
    // a cold page load every component asks at once, all of them miss this empty cache
    // before the first response lands, and each fires its own /settings GET. Coalescing is
    // what turns those into one. Safe here for the same reason as the company profile —
    // the shared promise lives only until the request settles.
    return this._coalesce(`businessSettings:${companyId}`, () => this._fetchBusinessSettings(companyId));
  }

  async _fetchBusinessSettings(companyId) {
    const now = Date.now();
    const generation = this.generation;
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', companyId)
        .limit(1);

      if (error) {
        console.error('Error fetching business settings:', error);
        return null;
      }

      if (data && data.length > 0) {
        if (generation !== this.generation) return data[0]; // written meanwhile: don't cache
        this.cachedBusinessSettings = data[0];
        this.lastFetch = now;
        // company_settings.timezone was write-only — editable in Business Settings and read
        // by nothing, so all dates rendered in the BROWSER's timezone. Publish it to the
        // date formatters here, which is the one place every surface already loads settings
        // through. Until it is set they fall back to browser-local, i.e. previous behaviour.
        setCompanyTimeZone(this.cachedBusinessSettings.timezone);
        // date_format and time_format were write-only in exactly the same way the timezone
        // was: offered in Business Settings, saved, reloaded into the form, and read by
        // nothing — every date in the app rendered hardcoded 'en-US'. Published here, beside
        // the timezone, because this is the one place every surface already loads settings
        // through. Do NOT add a second publisher somewhere more convenient.
        setCompanyDateFormat(this.cachedBusinessSettings.date_format);
        setCompanyTimeFormat(this.cachedBusinessSettings.time_format);
        return this.cachedBusinessSettings;
      }
    } catch (error) {
      console.error('Error fetching business settings:', error);
      return null;
    }

    // No settings found
    return null;
  }

  // Get company profile from companies table
  async getCompanyProfile(companyId, { fresh = false } = {}) {
    // Board #3248 made this coalesce-only, deliberately NOT cached, because
    // CompanyProfileSettingsTab writes the `companies` row directly and nothing would have
    // invalidated a cache. That gap is closed at the transport now: every successful write
    // to companies / settings / company_settings, from any component, fires
    // COMPANY_DATA_WRITE_EVENT (utils/supabaseClient.js) and clears this cache in this tab
    // and the others. So a sign-in or page load reads the row ONCE (DB-load fix, 2026-10-22:
    // the same row was read 4-6 times per sign-in) and an edit still shows on the next read.
    // Cached for companyProfileTtl (60s) only, because an edit on another device is invisible.
    // { fresh: true } skips the cached copy and refills it (sign-in uses it).
    const now = Date.now();
    const cached = this.companyProfileCache;
    if (!fresh && cached && cached.companyId === companyId && now - cached.at < this.companyProfileTtl) {
      return cached.data;
    }
    return this._coalesce(`companyProfile:${companyId}`, async () => {
      const generation = this.generation;
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .limit(1);

        if (error) {
          console.error('Error fetching company profile:', error);
          return null;
        }

        if (data && data.length > 0) {
          if (generation === this.generation) {
            this.companyProfileCache = { companyId, data: data[0], at: Date.now() };
          }
          return data[0];
        }

        return data;
      } catch (error) {
        console.error('Error fetching company profile:', error);
        return null;
      }
    });
  }

  // Get integrations - temporarily return empty array since integration_settings table doesn't exist in locked schema
  async getIntegrations(companyId) {
    try {
      // TODO: Add integrations support to company_settings table or create separate table
      return [];
    } catch (error) {
      console.error('Error fetching integrations:', error);
      return [];
    }
  }

  // Legacy method for backward compatibility - combines all settings
  async getSettings(companyId) {
    try {
      const businessSettings = await this.getBusinessSettings(companyId);
      const companyProfile = await this.getCompanyProfile(companyId);
      const integrations = await this.getIntegrations(companyId);

      return {
        businessSettings,
        companyProfile,
        integrations,
        // Legacy compatibility
        ...businessSettings,
        company: companyProfile
      };
    } catch (error) {
      console.error('Error getting combined settings:', error);
      return {
        businessSettings: null,
        companyProfile: null,
        integrations: [],
        company: null
      };
    }
  }

  // Update business settings (settings table, creates row if missing)
  async updateBusinessSettings(companyId, updates) {
    try {
      // Sanitize data before saving
      const sanitizedUpdates = this.sanitizeData(updates);

      // Ensure a settings row exists for this company
      const { data: existing, error: selErr } = await supabase
        .from('settings')
        .select('company_id')
        .eq('company_id', companyId)
        .limit(1);
      if (selErr) {
        console.warn('updateBusinessSettings: settings existence check failed', selErr);
      }

      if (!existing || existing.length === 0) {
        const { error: insErr } = await supabase
          .from('settings')
          .insert({ company_id: companyId, created_at: new Date().toISOString() });
        if (insErr) {
          console.warn('updateBusinessSettings: failed to insert settings shell row', insErr);
        }
      }

      // Filter to real `settings` columns so an unknown UI key can't reject the whole
      // UPDATE (PGRST204) and silently fail the save.
      const { kept: settingsUpdates, dropped } = filterToSettingsColumns(sanitizedUpdates);
      if (dropped.length > 0) {
        console.warn('updateBusinessSettings: dropped non-settings columns (no backing column):', dropped);
      }

      const { data, error } = await supabase
        .from('settings')
        .update({
          ...settingsUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) {
        console.error('Error updating business settings:', error);
        return false;
      }

      // Update cache
      this.cachedBusinessSettings = data;
      this.lastFetch = Date.now();
      return true;
    } catch (error) {
      console.error('Error updating business settings:', error);
      return false;
    }
  }

  // Update company profile
  async updateCompanyProfile(companyId, updates) {
    try {
      // Sanitize data before saving
      const sanitizedUpdates = this.sanitizeData(updates);

      const { data, error } = await supabase
        .from('companies')
        .update({
          ...sanitizedUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId)
        .select()
        .single();

      if (error) {
        console.error('Error updating company profile:', error);
        return false;
      }

      // Update cache
      this.companyProfileCache = data ? { companyId, data, at: Date.now() } : null;
      return true;
    } catch (error) {
      console.error('Error updating company profile:', error);
      return false;
    }
  }

  // Add or update integration setting - temporarily disabled since integration_settings table doesn't exist
  async updateIntegrationSetting(companyId, serviceName, config) {
    try {
      // TODO: Add integrations support to company_settings table or create separate table
      return false;
    } catch (error) {
      console.error('Error updating integration setting:', error);
      return false;
    }
  }

  // Get invoice configuration (combines business settings and company profile for invoicing)
  async getInvoiceConfig(companyId) {
    try {
      const [businessSettings, companyProfile] = await Promise.all([
        this.getBusinessSettings(companyId),
        this.getCompanyProfile(companyId)
      ]);

      // Return invoice-specific configuration
      return {
        // Invoice numbering — canonical home is company_settings (the generate_document_number
        // RPC reads it). companies.invoice_prefix/invoice_start_number is a retired legacy mirror.
        invoicePrefix: businessSettings?.invoice_number_prefix || 'INV',
        invoiceStartNumber: businessSettings?.next_invoice_number || 1,

        // Company details for invoice header
        companyName: companyProfile?.name || 'Your Company',
        companyAddress: companyProfile?.address || '',
        companyCity: companyProfile?.city || '',
        companyState: companyProfile?.state || '',
        companyZip: companyProfile?.zip_code || '',
        companyPhone: companyProfile?.phone || '',
        companyEmail: companyProfile?.email || '',

        // NOTE: companies.currency is deliberately NOT read here. This same object sets
        // `currency` from company_settings further down and the later key wins, so this
        // line never had any effect -- the build has been reporting it as no-dupe-keys.
        // company_settings is the canonical home for settings fields and the same-named
        // columns on `companies` are shadows (same story as default_tax_rate). Both hold
        // 'USD' for all 263 companies today, so removing this changes no output; it
        // removes the second source for one fact, which is what makes them diverge later.

        // Invoice settings from business settings
        defaultPaymentTerms: businessSettings?.default_payment_terms || DEFAULT_PAYMENT_TERMS,
        // Canonical company-default tax rate is company_settings.default_tax_rate (percent).
        // The old `tax_rate` shadow column is being retired — read the canonical one.
        taxRate: businessSettings?.default_tax_rate ?? 0,
        lateFeeRate: businessSettings?.late_fee_rate || 0,

        // Invoice template settings
        showLogo: businessSettings?.show_logo_on_invoices !== false,
        showNotes: businessSettings?.show_notes_on_invoices !== false,
        showTerms: businessSettings?.show_terms_on_invoices !== false,

        // Default invoice notes and terms
        defaultNotes: businessSettings?.default_invoice_notes || '',
        defaultTerms: businessSettings?.default_invoice_terms || 'Payment is due within 30 days of invoice date.',

        // Currency and formatting
        currency: businessSettings?.currency || 'USD',
        currencySymbol: businessSettings?.currency_symbol || '$',

        // Footer + payment instructions (canonical: company_settings). Emitted in snake_case
        // to match InvoiceTemplate's read, so it uses these instead of the legacy companies copy.
        invoice_footer: businessSettings?.invoice_footer || '',
        payment_instructions: businessSettings?.payment_instructions || ''
      };
    } catch (error) {
      console.error('Error fetching invoice config:', error);
      // Return safe defaults
      return {
        invoicePrefix: 'INV',
        invoiceStartNumber: 1,
        companyName: 'Your Company',
        companyAddress: '',
        companyCity: '',
        companyState: '',
        companyZip: '',
        companyPhone: '',
        companyEmail: '',
        defaultPaymentTerms: DEFAULT_PAYMENT_TERMS,
        taxRate: 0,
        lateFeeRate: 0,
        showLogo: true,
        showNotes: true,
        showTerms: true,
        defaultNotes: '',
        defaultTerms: 'Payment is due within 30 days of invoice date.',
        currency: 'USD',
        currencySymbol: '$'
      };
    }
  }

  // Get rates and pricing settings (industry standard - uses rate_cards table)
  async getRatesPricingSettings(companyId) {
    try {
      // STEP 1: Load from rate_cards table (exists in locked schema)
      const today = companyDateKey();
      const { data: rateCards, error: rateError } = await supabase
        .from('rate_cards')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .lte('effective_date', today)
        .or(`expiration_date.is.null,expiration_date.gte.${today}`)
        .order('effective_date', { ascending: false });

      if (rateError && rateError.code !== 'PGRST116') {
        console.warn('Error fetching rate_cards:', rateError);
      }

      // STEP 2 REMOVED — board #1310, operator ruling 2026-09-28.
      //
      // There used to be a branch here that took rateCards[0] as a "primary card" and built
      // the company's whole labor rate from it. Two things were wrong with it.
      //
      // First, it could never work. It read `primaryCard.base_rate` and
      // `primaryCard.overtime_multiplier`, and neither is what the app stores: rate_cards
      // carries a LEGACY `base_rate` column alongside the canonical `rate` that RateCards
      // settings actually writes, and it has NO multiplier columns at all. Measured live
      // 2026-09-29: of 27 active rate cards, 0 have base_rate and 27 have rate. So
      // parseFloat(null) made `standard` NaN, `overtime` NaN, and QuoteBuilder's
      // `settings.laborRates?.standard || 75` then swallowed the NaN and quoted the
      // hardcoded $75/1.5x. A company with rate cards got neither its rate cards NOR its
      // settings — silently, on every quote.
      //
      // Second, even fixed, it was the wrong model. The ruling: Settings -> Rates & Pricing
      // is authoritative for the company-wide labor base rate and all four multipliers;
      // rate cards are PER-SERVICE overrides, not a global primary rate. That also honours
      // P10 (rate_cards = advanced pricing rules, not a second catalog) and matches the
      // Jobber/HCP global-default-rate + per-service-rate-items pattern.
      //
      // The cards are still fetched and returned below for per-service use; what is gone is
      // one of them standing in for the company.

      // THE company-wide labor rate — not a fallback any more. Every company reaches this.
      const { data: appSettings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        console.warn('Error fetching settings:', settingsError);
      }

      // Board #1310. All four multipliers come from Settings -> Rates & Pricing, which the
      // ruling makes authoritative. weekend and holiday used to be hardcoded 1.25 and 1.5
      // here, so those two columns existed, were editable, and were ignored.
      // `??` throughout, not `||`: a deliberate 1x multiplier (a company that does not
      // charge an overtime premium) is a legitimate setting and must not be replaced by a
      // fallback — the same falsy-zero trap as the tax rate above.
      const laborRate = parseFloat(appSettings?.labor_rate ?? 75);
      const overtimeMultiplier = parseFloat(appSettings?.overtime_multiplier ?? 1.5);
      const weekendMultiplier = parseFloat(appSettings?.weekend_multiplier ?? 1.25);
      const holidayMultiplier = parseFloat(appSettings?.holiday_multiplier ?? 1.5);
      const partsMarkup = appSettings?.material_markup != null ? parseFloat(appSettings.material_markup) : 0;  // Allow 0 value
      const subcontractorMarkup = appSettings?.subcontractor_markup != null ? parseFloat(appSettings.subcontractor_markup) : 0;
      const emergencyMultiplier = parseFloat(appSettings?.emergency_rate_multiplier ?? 1.5);
      const travelCharge = parseFloat(appSettings?.travel_charge_per_mile ?? 0.65);
      const minTravel = parseFloat(appSettings?.minimum_travel_charge ?? 25.00);
      const cancelFee = parseFloat(appSettings?.cancellation_fee ?? 50.00);
      const taxRate = parseFloat(appSettings?.default_tax_rate ?? 0);  // DEFAULT TO 0 (no tax) - auto-detect by zip code

      return {
        // The cards are returned for PER-SERVICE overrides at the matching line. This used
        // to be [] on this path, which is why nothing downstream could apply them per
        // service even when a company had them (#1310).
        rateCards: rateCards || [],
        laborRates: {
          standard: laborRate,
          overtime: laborRate * overtimeMultiplier,
          emergency: laborRate * emergencyMultiplier,
          weekend: laborRate * weekendMultiplier,
          holiday: laborRate * holidayMultiplier
        },
        markupPercentages: {
          materials: partsMarkup,
          // Was a hardcoded 15 on this path while the rate-card path read the company's own
          // settings.subcontractor_markup — so the same company got a different subcontractor
          // markup depending on which branch it fell into.
          subcontractor: subcontractorMarkup
        },
        taxRate: taxRate,
        travelChargePerMile: travelCharge,
        minimumTravelCharge: minTravel,
        cancellationFee: cancelFee,
        emergencyRateMultiplier: emergencyMultiplier
      };
    } catch (error) {
      console.error('❌ Error fetching rates pricing settings:', error);
      // Return safe defaults on error (use 0% markup as default, not 25%)
      return {
        rateCards: [],
        laborRates: {
          standard: 75,
          overtime: 112.5,
          emergency: 150,
          weekend: 93.75,
          holiday: 112.5
        },
        markupPercentages: {
          materials: 0,  // Default to 0% markup (user can configure in settings)
          subcontractor: 0
        },
        taxRate: 0,  // DEFAULT TO 0 (no tax) - auto-detect by zip code
        travelChargePerMile: 0.65,
        minimumTravelCharge: 25.00,
        cancellationFee: 50.00,
        emergencyRateMultiplier: 1.5
      };
    }
  }

  // Get next invoice number (from company_settings — the canonical numbering home the
  // generate_document_number RPC reads; companies.invoice_* is a retired legacy mirror).
  async getNextInvoiceNumber(companyId) {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .select('next_invoice_number, invoice_number_prefix')
        .eq('company_id', companyId)
        .single();

      if (error) {
        console.error('Error fetching invoice number:', error);
        return { number: 1, prefix: 'INV' };
      }

      // For now, return the start number (in production, you'd track the current number)
      return {
        number: data.next_invoice_number || 1,
        prefix: data.invoice_number_prefix || 'INV'
      };
    } catch (error) {
      console.error('Error fetching invoice number:', error);
      return { number: 1, prefix: 'INV' };
    }
  }

  // Generate the next invoice number. Delegates to the unified DB generator
  // (generate_document_number) so the number is assigned atomically inside
  // Postgres under a FOR UPDATE lock — identical and race-safe across every
  // device/surface, reading the per-company counter in
  // company_settings.invoice_number_prefix / next_invoice_number. This is the
  // same generator and settable-counter pattern quotes use (next_quote_number).
  async getAndIncrementInvoiceNumber(companyId) {
    try {
      if (!companyId) throw new Error('Missing companyId');

      const { data, error } = await supabase.rpc('generate_document_number', {
        p_company_id: companyId,
        p_doc_type: 'invoice'
      });
      if (error) throw error;
      if (!data) throw new Error('generate_document_number returned no value');

      return data;
    } catch (e) {
      console.warn('getAndIncrementInvoiceNumber: fallback generator used', e?.message || e);
      // Fallback generator similar to InvoicesService.generateInvoiceNumber
      const ms2 = String(Date.now() % 100).padStart(2, '0');
      const r2 = String(Math.floor(Math.random() * 90) + 10); // 10-99
      const year = new Date().getFullYear();
      return `INV-${year}-${ms2}${r2}`;
    }
  }


  // Clear cache (useful after settings updates)
  // Also called on sign-out, so the next session (possibly another company) starts empty.
  clearCache() {
    this.generation += 1;
    this.inFlight.clear();
    this.cachedBusinessSettings = null;
    this.companyProfileCache = null;
    this.cachedIntegrations = null;
    this.lastFetch = null;
  }

  // Refresh settings from database
  async refreshSettings(companyId) {
    this.clearCache();
    return await this.getSettings(companyId);
  }

  // Set integration flag (enable/disable integrations)
  async setIntegrationFlag(companyId, flagKey, value) {
    try {
      if (!companyId) throw new Error('Missing companyId');

      // Map the flag key to the database column name
      const columnMap = {
        enableQuickbooks: 'enable_quickbooks',
        enableSms: 'enable_sms',
        enableGoogleCalendar: 'enable_google_calendar',
        enableCloudStorage: 'enable_cloud_storage',
        enableCrm: 'enable_crm',
        enableZapier: 'enable_zapier',
        enableStripeConnect: 'enable_stripe_connect',
        enableMailchimp: 'enable_mailchimp',
        enableXero: 'enable_xero',
        enableGoogleMaps: 'enable_google_maps',
        enableDocuSign: 'enable_docusign',
        enablePandaDoc: 'enable_pandadoc',
        enableClockify: 'enable_clockify'
      };

      const columnName = columnMap[flagKey];
      if (!columnName) {
        console.warn(`Unknown integration flag: ${flagKey}`);
        return false;
      }

      // Ensure settings row exists
      let { data: existing } = await supabase
        .from('settings')
        .select('company_id')
        .eq('company_id', companyId)
        .limit(1);

      if (!existing || existing.length === 0) {
        // Create settings row if it doesn't exist
        const { error: createErr } = await supabase
          .from('settings')
          .insert({ company_id: companyId, [columnName]: value });
        if (createErr) {
          console.error('Error creating settings row:', createErr);
          return false;
        }
      } else {
        // Update existing settings row
        const { error: updateErr } = await supabase
          .from('settings')
          .update({ [columnName]: value })
          .eq('company_id', companyId);
        if (updateErr) {
          console.error('Error updating integration flag:', updateErr);
          return false;
        }
      }

      // Clear cache so next fetch gets fresh data
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Error setting integration flag:', error);
      return false;
    }
  }
}

// Export singleton instance
const settingsService = new SettingsService();
export default settingsService;
